import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Search, ChevronUp, ChevronDown, Download } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Input } from './ui/input';
import { Button } from './ui/button';

export function DataTable({
  data,
  columns,
  emptyMessage = null,
  showGlobalSearch = true,
  showPagination = true,
  pageSize = 16,
  customFilters = null,
  enableRowSelection = false,
  rowSelection = {},
  onRowSelectionChange = null,
  canSelectRow = null,
  bulkActionsBar = null,
}) {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: pageSize,
  });

  // Update pagination when pageSize prop changes
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      pageIndex: 0,
      pageSize: pageSize,
    }));
  }, [pageSize]);

  // Auto-hide columns with meta.hidden
  const hiddenColumns = columns.reduce((acc, col) => {
    if (col.meta?.hidden) {
      acc[col.id || col.accessorKey] = false;
    }
    return acc;
  }, {});

  // Build selection column if enabled
  const selectionColumn = enableRowSelection ? [{
    id: 'select',
    size: 40,
    header: ({ table }) => {
      const rows = table.getFilteredRowModel().rows;
      const selectableRows = canSelectRow ? rows.filter(row => canSelectRow(row.original)) : rows;
      const allSelected = selectableRows.length > 0 && selectableRows.every(row => row.getIsSelected());
      const someSelected = selectableRows.some(row => row.getIsSelected());
      return (
        <Checkbox
          checked={allSelected}
          onCheckedChange={(value) => {
            selectableRows.forEach(row => row.toggleSelected(!!value));
          }}
          aria-label={t('Select all')}
          {...(someSelected && !allSelected ? { 'data-state': 'indeterminate' } : {})}
        />
      );
    },
    cell: ({ row }) => {
      const selectable = canSelectRow ? canSelectRow(row.original) : true;
      if (!selectable) return null;
      return (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={t('Select the row')}
        />
      );
    },
    enableSorting: false,
  }] : [];

  const allColumns = [...selectionColumn, ...columns];

  const table = useReactTable({
    data,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    ...(enableRowSelection ? {
      enableRowSelection: canSelectRow ? (row) => canSelectRow(row.original) : true,
      onRowSelectionChange: onRowSelectionChange,
    } : {}),
    state: {
      sorting,
      globalFilter,
      columnFilters,
      pagination,
      columnVisibility: hiddenColumns,
      ...(enableRowSelection ? { rowSelection } : {}),
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: pageSize,
      },
    },
    globalFilterFn: 'includesString',
  });

  // Debug logs
  console.log('DataTable Debug:', {
    totalRows: data.length,
    filteredRows: table.getFilteredRowModel().rows.length,
    pageCount: table.getPageCount(),
    currentPage: table.getState().pagination.pageIndex,
    pageSize: table.getState().pagination.pageSize,
    showPagination,
  });

  // CSV Export function
  const exportToCSV = () => {
    const filteredRows = table.getFilteredRowModel().rows;

    if (filteredRows.length === 0) {
      alert(t('No data to export'));
      return;
    }

    // Get visible columns (exclude action and select columns)
    const visibleColumns = table.getAllColumns().filter(col =>
      col.getIsVisible() && col.id !== 'actions' && col.id !== 'select' && col.columnDef.header
    );

    // Build CSV header
    const headers = visibleColumns.map(col => {
      const header = col.columnDef.header;
      return typeof header === 'string' ? header : col.id;
    });

    // Build CSV rows
    const csvRows = filteredRows.map(row => {
      return visibleColumns.map(col => {
        const cell = row.getValue(col.id);
        // Handle null/undefined
        if (cell === null || cell === undefined) return '';
        // Convert to string and escape quotes
        const value = String(cell).replace(/"/g, '""');
        // Wrap in quotes if contains comma, newline, or quotes
        return /[",\n]/.test(value) ? `"${value}"` : value;
      });
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-4">
      {/* Global Search and Custom Filters */}
      <div className="flex gap-4 flex-col sm:flex-row">
        {showGlobalSearch && (
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder={t('Search…')}
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-10"
            />
          </div>
        )}
        {customFilters && (
          <div className="flex gap-2 flex-wrap">
            {customFilters({ table, columnFilters, setColumnFilters })}
          </div>
        )}
        <Button
          onClick={exportToCSV}
          variant="outline"
          className="flex items-center gap-2 whitespace-nowrap"
        >
          <Download className="w-4 h-4" />
          {t('Export CSV')}
        </Button>
      </div>

      {/* Bulk Actions Bar */}
      {enableRowSelection && bulkActionsBar && Object.keys(rowSelection).length > 0 && (
        bulkActionsBar({ table, selectedCount: Object.keys(rowSelection).length })
      )}

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const width = header.column.columnDef.size;
                  return (
                    <TableHead
                      key={header.id}
                      style={width ? { width: `${width}px` } : undefined}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={
                            header.column.getCanSort()
                              ? 'flex items-center gap-2 cursor-pointer select-none'
                              : ''
                          }
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <span className="text-gray-400">
                              {{
                                asc: <ChevronUp className="w-4 h-4" />,
                                desc: <ChevronDown className="w-4 h-4" />,
                              }[header.column.getIsSorted()] ?? (
                                <div className="flex flex-col">
                                  <ChevronUp className="w-3 h-3 -mb-1" />
                                  <ChevronDown className="w-3 h-3" />
                                </div>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => {
                    const width = cell.column.columnDef.size;
                    return (
                      <TableCell
                        key={cell.id}
                        style={width ? { width: `${width}px` } : undefined}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={allColumns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage || t('No data available')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Results count and Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {table.getFilteredRowModel().rows.length > 0 ? (
            <>
              {showPagination ? (
                <>
                  {t('Showing {{count}} of {{total}} results', {
                    count: table.getRowModel().rows.length,
                    total: table.getFilteredRowModel().rows.length,
                  })}
                  {globalFilter && ` ${t('(filtered from {{count}})', { count: data.length })}`}
                </>
              ) : (
                <>
                  {t('Showing {{count}} results', { count: table.getFilteredRowModel().rows.length })}
                  {globalFilter && ` ${t('(filtered from {{count}} total)', { count: data.length })}`}
                </>
              )}
            </>
          ) : null}
        </div>

        {showPagination && table.getPageCount() > 1 && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {t('Previous')}
            </Button>
            <div className="text-sm text-muted-foreground">
              {t('Page {{current}} of {{total}}', {
                current: table.getState().pagination.pageIndex + 1,
                total: table.getPageCount(),
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {t('Next')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
